import { css } from "@emotion/css";
import { Divider } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import { Repository } from "@octopusdeploy/octopus-server-client";
import type { GitHubAppAuthorizationStatus, GitHubAppAuthorizationStatusWithUserDetails, GitHubAppConnections, GitHubAppInstallations } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import type { PageContentCalloutType } from "~/components/PageContent/PageContent";
import { PageContent } from "~/components/PageContent/PageContent";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { GitConnectionAnalyticView } from "./Analytics/GitConnectionAnalyticView";
import { GitHubAppAuthCheck } from "./GitHubAppAuthCheck";
import GitHubAppConnectionList from "./GitHubAppConnectionList";
import { GitHubUserCard } from "./GitHubUserCard";
import { GitConnectionAuthorizeOnboarding, GitConnectionInstallOnboarding } from "./Onboarding/GitConnectionOnboarding";
import { ConfigureGitHubAppLink } from "./Onboarding/GitHubAppLinks";
interface GitHubConnectionsPageProps {
    spaceId: string;
    loaderData: GitHubConnectionsPageLoaderData;
}
interface GitHubConnectionsPageLoaderData {
    connections: GitHubAppConnections;
    notConnectedInstallations: GitHubAppInstallations | undefined;
    authorizationStatus: GitHubAppAuthorizationStatus | GitHubAppAuthorizationStatusWithUserDetails;
}
async function GetAuthorizationStatus(repository: Repository, isGitHubUserCardEnabled: boolean): Promise<GitHubAppAuthorizationStatus | GitHubAppAuthorizationStatusWithUserDetails> {
    if (isGitHubUserCardEnabled) {
        return await repository.GitHubApp.getAuthorizationStatusWithUserDetails();
    }
    else {
        return await repository.GitHubApp.getAuthorizationStatus();
    }
}
async function GetNotConnectedInstallations(repository: Repository, isAuthorized: boolean): Promise<GitHubAppInstallations | undefined> {
    if (isAuthorized) {
        return await repository.GitHubApp.getInstallations(true); // `true` skips any installations that are already connected.
    }
    else {
        return undefined;
    }
}
export async function gitHubConnectionsPageLoader(repository: Repository, isGitHubUserCardEnabled: boolean): Promise<GitHubConnectionsPageLoaderData> {
    const connections = repository.GitHubApp.getConnections(0, Repository.takeAll);
    const authorizationStatus = await GetAuthorizationStatus(repository, isGitHubUserCardEnabled);
    const notConnectedInstallations = await GetNotConnectedInstallations(repository, authorizationStatus.IsAuthorized);
    return { connections: await connections, authorizationStatus, notConnectedInstallations };
}
export function GitHubConnectionsPage({ spaceId, loaderData }: GitHubConnectionsPageProps) {
    const isGitHubUserCardEnabled = useOctopusFeatureToggle("github-user-card", false);
    const { connections, notConnectedInstallations: initialNotConnectedInstallations, authorizationStatus: initialAuthorizationStatus } = loaderData;
    const { result: authorizationStatus, refetch: refetchAuthorizationStatus } = useQuery(async (repository) => {
        return await GetAuthorizationStatus(repository, isGitHubUserCardEnabled);
    }, [isGitHubUserCardEnabled], "Get authorization status for GitHub user", { initialResult: initialAuthorizationStatus });
    const { result: notConnectedInstallations } = useQuery(async (repository) => {
        return await GetNotConnectedInstallations(repository, authorizationStatus.IsAuthorized);
    }, [authorizationStatus.IsAuthorized], "Get not connected installations for GitHub user", { initialResult: initialNotConnectedInstallations });
    if (connections.Connections.length === 0) {
        // If we don't have any existing connections we want to show some sort of full-screen onboarding
        // because it's likely this is the first time they've hit this page. This covers all of the cases
        // where the generic list handling in the main return would leave users a little bit lost.
        // If we have no connections or installations, show the installation onboarding
        if (!authorizationStatus.IsAuthorized) {
            // If we have no existing connections and the user is not authorized, show the authorize onboarding.
            return <GitConnectionAuthorizeOnboarding />;
        }
        if (notConnectedInstallations === undefined) {
            // If we have no connections, and the user is authorized but haven't finished loading the installations
            // from GitHub, don't show anything yet.
            return null;
        }
        if (notConnectedInstallations.Installations.length === 0) {
            // If we have no connections or installations, show the installation onboarding
            return (<PageContent header={{ title: "GitHub Connections" }}>
                    <GitHubUserSection authorizationStatus={authorizationStatus} refetchAuthorizationStatus={refetchAuthorizationStatus}/>
                    <BorderedContent>
                        <GitConnectionInstallOnboarding />
                    </BorderedContent>
                </PageContent>);
        }
    }
    const callout: PageContentCalloutType | undefined = isGitHubUserCardEnabled ? undefined : { type: "custom", content: <GitHubAppAuthCheck actionDescription={"view other organisations and accounts"}/> };
    return (<PageContent header={{ title: "GitHub Connections" }} callout={callout}>
            <GitHubUserSection authorizationStatus={authorizationStatus} refetchAuthorizationStatus={refetchAuthorizationStatus}/>
            <BorderedContent>
                <GitConnectionAnalyticView name="View GitHub connections list"/>
                <GitHubAppConnectionList spaceId={spaceId} connections={connections} notConnectedInstallations={notConnectedInstallations}/>
                <GitConnectionsInstallFooter userIsAuthorized={authorizationStatus.IsAuthorized}/>
            </BorderedContent>
        </PageContent>);
}
interface GitHubUserSectionProps {
    authorizationStatus: GitHubAppAuthorizationStatus | GitHubAppAuthorizationStatusWithUserDetails;
    refetchAuthorizationStatus: () => void;
}
function GitHubUserSection({ authorizationStatus, refetchAuthorizationStatus }: GitHubUserSectionProps) {
    if (!useOctopusFeatureToggle("github-user-card", false)) {
        return null;
    }
    return (<>
            <h2>Current User</h2>
            <GitHubUserCard authorizationStatus={authorizationStatus} onUserUpdated={refetchAuthorizationStatus}/>
            <h2>Connections</h2>
        </>);
}
interface GitConnectionsInstallFooterProps {
    userIsAuthorized: boolean;
}
function GitConnectionsInstallFooter({ userIsAuthorized }: GitConnectionsInstallFooterProps) {
    const styles = {
        footer: css({
            marginLeft: space[16],
            marginRight: space[16],
            marginTop: space[24],
            marginBottom: space[24],
            textAlign: "center",
        }),
    };
    if (!userIsAuthorized) {
        return null;
    }
    return (<>
            <Divider />
            <div className={styles.footer}>
                <p>Install the GitHub app to connect to other organizations or users</p>
                <p>
                    <ConfigureGitHubAppLink />
                </p>
            </div>
        </>);
}
